import type {
  ScoutDashboardMetricsQuery,
  ScoutDashboardMetricsQueryVariables,
} from "generated-graphql/graphql";

import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { CommandButton } from "components/CommandButton";
import ScoutIcon from "components/ScoutIcon";
import { gql } from "generated-graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { prettyPrintDateTime } from "util/dates";

import {
  DEFAULT_SCOUT_DASHBOARD_CHART_SETTINGS,
  ManageScoutChartsPopover,
  createDefaultScoutDashboardChartConfig,
  deleteScoutDashboardChartConfig,
  getScoutDashboardChartStorageKey,
  getVisibleScoutDashboardChartItems,
  normalizeScoutDashboardChartSettings,
  saveScoutDashboardChartConfig,
  selectScoutDashboardChartConfig,
  setCurrentScoutDashboardChartConfig,
  type ScoutDashboardChartConfig,
  type ScoutDashboardChartSettings,
} from "./ScoutDashboardManageCharts";
import { ScoutDashboardStatCard } from "./ScoutDashboardStatCard";
import { ScoutDashboardTimeSeriesCard } from "./ScoutDashboardTimeSeriesCard";

const SCOUT_DASHBOARD_METRICS_QUERY = gql(`
  query ScoutDashboardMetrics($input: ScoutDashboardMetricsInput!) {
    scoutDashboardMetrics(input: $input) {
      startTime
      endTime
      periodSeconds
      widgets {
        id
        title
        kind
        unit
        documentation {
          badges
          details
        }
        yAxisMin
        yAxisMax
        referenceLines {
          value
          label
          color
        }
        series {
          id
          label
          unit
          points {
            timestamp
            value
          }
        }
      }
    }
  }
`);

type RangeKey = "1h" | "3h" | "12h" | "1d" | "3d" | "1w";
type RangeOption = {
  key: RangeKey;
  label: string;
  durationMs: number;
  periodSeconds: number;
};
type RangeState = ReturnType<typeof getRangeState>;
type RangeRequestSource = "initial" | "manual" | "poll";
type RangeRequestState = {
  displayRangeState: RangeState;
  requestedRangeState: RangeState;
  requestSource: RangeRequestSource;
};
type ScoutDashboardWidget = ScoutDashboardMetricsQuery["scoutDashboardMetrics"]["widgets"][number];
const EMPTY_SCOUT_DASHBOARD_WIDGETS: ScoutDashboardWidget[] = [];

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const DASHBOARD_REFRESH_INTERVAL_MS = 60 * 1000;
const SINGLE_VALUE_WIDGET_KIND = "SINGLE_VALUE";
const TIME_SERIES_WIDGET_KIND = "TIME_SERIES";

/**
 * Keep the selectable range at one week or less. The API uses CloudWatch SEARCH
 * for dynamic node/task metrics, and SEARCH only discovers metrics that have
 * reported in the past two weeks; wider ranges also increase GetMetricData
 * datapoint volume across every discovered dynamic series.
 */
const RANGE_OPTIONS = [
  { key: "1h", label: "1Hr", durationMs: HOUR_MS, periodSeconds: 60 },
  { key: "3h", label: "3Hr", durationMs: 3 * HOUR_MS, periodSeconds: 60 },
  { key: "12h", label: "12Hr", durationMs: 12 * HOUR_MS, periodSeconds: 300 },
  { key: "1d", label: "1d", durationMs: DAY_MS, periodSeconds: 1200 },
  { key: "3d", label: "3d", durationMs: 3 * DAY_MS, periodSeconds: 2400 },
  { key: "1w", label: "1w", durationMs: 7 * DAY_MS, periodSeconds: 12000 },
] satisfies RangeOption[];

function readRangeKey(value: string | null): RangeKey {
  if (value === "1h") {
    return "1h";
  }
  if (value === "3h") {
    return "3h";
  }
  if (value === "12h") {
    return "12h";
  }
  if (value === "1d") {
    return "1d";
  }
  if (value === "3d") {
    return "3d";
  }
  if (value === "1w") {
    return "1w";
  }
  return "1h";
}

function getRangeOption(key: RangeKey): RangeOption {
  const option = RANGE_OPTIONS.find((candidate) => candidate.key === key);
  return option ?? RANGE_OPTIONS[0];
}

function getRangeState(params: URLSearchParams, now: Date) {
  const rangeKey = readRangeKey(params.get("range"));
  const rangeOption = getRangeOption(rangeKey);
  const durationMs = rangeOption.durationMs;
  const endTime = now;
  const startTime = new Date(endTime.getTime() - durationMs);

  return {
    rangeKey,
    startTime,
    endTime,
    periodSeconds: rangeOption.periodSeconds,
  };
}

function makeSearchParams(input: { rangeKey: RangeKey }): URLSearchParams {
  const next = new URLSearchParams();
  next.set("range", input.rangeKey);
  return next;
}

export function ScoutDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useCurrentUser();
  const [rangeRequestState, setRangeRequestState] = useState<RangeRequestState>(() => {
    const initialRangeState = getRangeState(searchParams, new Date());
    return {
      displayRangeState: initialRangeState,
      requestedRangeState: initialRangeState,
      requestSource: "initial",
    };
  });
  const [hasManualRangeError, setHasManualRangeError] = useState(false);
  const [manageChartsAnchorEl, setManageChartsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { displayRangeState, requestedRangeState } = rangeRequestState;
  const chartSettingsStorageKey = getScoutDashboardChartStorageKey(currentUser.user?.email);
  const [chartSettings, setChartSettings] = useLocalStorageState<ScoutDashboardChartSettings>(
    chartSettingsStorageKey,
    { defaultValue: DEFAULT_SCOUT_DASHBOARD_CHART_SETTINGS }
  );
  const queryVariables = useMemo<ScoutDashboardMetricsQueryVariables>(
    () => ({
      input: {
        startTime: requestedRangeState.startTime.toISOString(),
        endTime: requestedRangeState.endTime.toISOString(),
        periodSeconds: requestedRangeState.periodSeconds,
      },
    }),
    [requestedRangeState.endTime, requestedRangeState.periodSeconds, requestedRangeState.startTime]
  );

  const handleDashboardMetricsCompleted = useCallback(() => {
    if (rangeRequestState.requestSource === "manual") {
      setSearchParams(makeSearchParams({ rangeKey: requestedRangeState.rangeKey }));
    }

    setHasManualRangeError(false);
    setRangeRequestState((previousState) => {
      if (
        previousState.requestSource === "initial" &&
        previousState.displayRangeState === previousState.requestedRangeState
      ) {
        return previousState;
      }

      return {
        ...previousState,
        displayRangeState: previousState.requestedRangeState,
        requestSource: "initial",
      };
    });
  }, [rangeRequestState.requestSource, requestedRangeState.rangeKey, setSearchParams]);

  const handleDashboardMetricsError = useCallback(() => {
    if (rangeRequestState.requestSource !== "manual") {
      return;
    }

    setHasManualRangeError(true);
    setRangeRequestState((previousState) => ({
      ...previousState,
      requestedRangeState: previousState.displayRangeState,
      requestSource: "initial",
    }));
  }, [rangeRequestState.requestSource]);

  const { data, previousData, loading, error } = useQuery<
    ScoutDashboardMetricsQuery,
    ScoutDashboardMetricsQueryVariables
  >(SCOUT_DASHBOARD_METRICS_QUERY, {
    variables: queryVariables,
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted: handleDashboardMetricsCompleted,
    onError: handleDashboardMetricsError,
  });

  const isManualRangeLoading = rangeRequestState.requestSource === "manual" && loading;

  useEffect(() => {
    if (isManualRangeLoading) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setRangeRequestState((previousState) => {
        const nextSearchParams = makeSearchParams({
          rangeKey: previousState.displayRangeState.rangeKey,
        });
        return {
          ...previousState,
          requestedRangeState: getRangeState(nextSearchParams, new Date()),
          requestSource: "poll",
        };
      });
    }, DASHBOARD_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isManualRangeLoading]);

  const dashboardData = data ?? previousData;
  const widgets = dashboardData?.scoutDashboardMetrics.widgets ?? EMPTY_SCOUT_DASHBOARD_WIDGETS;
  const statWidgets = useMemo(
    () => widgets.filter((widget) => widget.kind === SINGLE_VALUE_WIDGET_KIND),
    [widgets]
  );
  const chartWidgets = useMemo(
    () => widgets.filter((widget) => widget.kind === TIME_SERIES_WIDGET_KIND),
    [widgets]
  );
  const reservedChartSeriesCount = useMemo(
    () =>
      chartWidgets.reduce(
        (maxSeriesCount, widget) => Math.max(maxSeriesCount, widget.series.length),
        0
      ),
    [chartWidgets]
  );
  const chartIds = useMemo(() => chartWidgets.map((widget) => widget.id), [chartWidgets]);
  const normalizedChartSettings = useMemo(
    () => normalizeScoutDashboardChartSettings(chartSettings, chartIds),
    [chartIds, chartSettings]
  );
  const visibleChartWidgets = useMemo(
    () => getVisibleScoutDashboardChartItems(chartWidgets, normalizedChartSettings.current),
    [chartWidgets, normalizedChartSettings]
  );
  const chartOptions = useMemo(
    () =>
      chartWidgets.map((widget) => ({
        id: widget.id,
        title: widget.title,
      })),
    [chartWidgets]
  );
  const showManageChartsPopover = Boolean(manageChartsAnchorEl);
  const isInitialLoading = loading && !dashboardData;
  const hasDashboardError = Boolean(error) || hasManualRangeError;
  const canShowWidgets = !hasDashboardError || Boolean(dashboardData);
  const showEmptyState =
    canShowWidgets && !hasDashboardError && !isInitialLoading && widgets.length === 0;
  const rangeDurationMs =
    displayRangeState.endTime.getTime() - displayRangeState.startTime.getTime();
  const displayRangeKey = [
    displayRangeState.rangeKey,
    displayRangeState.startTime.toISOString(),
    displayRangeState.endTime.toISOString(),
    displayRangeState.periodSeconds,
  ].join("|");

  const updateRange = (nextRangeKey: RangeKey) => {
    setHasManualRangeError(false);
    setRangeRequestState((previousState) => ({
      ...previousState,
      requestedRangeState: getRangeState(makeSearchParams({ rangeKey: nextRangeKey }), new Date()),
      requestSource: "manual",
    }));
  };

  const handleCurrentChartConfigChange = useCallback(
    (config: ScoutDashboardChartConfig) => {
      setChartSettings((previousSettings) =>
        setCurrentScoutDashboardChartConfig(previousSettings, config, chartIds)
      );
    },
    [chartIds, setChartSettings]
  );

  const handleSaveChartConfig = useCallback(
    (name: string) => {
      setChartSettings((previousSettings) =>
        saveScoutDashboardChartConfig(previousSettings, name, chartIds)
      );
    },
    [chartIds, setChartSettings]
  );

  const handleDeleteChartConfig = useCallback(
    (name: string) => {
      setChartSettings((previousSettings) =>
        deleteScoutDashboardChartConfig(previousSettings, name, chartIds)
      );
    },
    [chartIds, setChartSettings]
  );

  const handleSelectChartConfig = useCallback(
    (name: string) => {
      setChartSettings((previousSettings) =>
        selectScoutDashboardChartConfig(previousSettings, name, chartIds)
      );
    },
    [chartIds, setChartSettings]
  );

  const handleResetChartConfig = useCallback(() => {
    setChartSettings((previousSettings) =>
      setCurrentScoutDashboardChartConfig(
        previousSettings,
        createDefaultScoutDashboardChartConfig(chartIds),
        chartIds
      )
    );
  }, [chartIds, setChartSettings]);

  return (
    <Stack spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
          <ScoutIcon sx={{ color: "encampBlue.main", width: 36, height: 36 }} />
          <Box minWidth={0}>
            <Typography variant="h4" fontWeight={600}>
              Scout Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {prettyPrintDateTime(displayRangeState.startTime.toISOString())} to{" "}
              {prettyPrintDateTime(displayRangeState.endTime.toISOString())}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <CommandButton
            disabled={chartOptions.length === 0}
            onClick={(event) => setManageChartsAnchorEl(event.currentTarget)}
          >
            Manage Charts
          </CommandButton>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={isManualRangeLoading ? requestedRangeState.rangeKey : displayRangeState.rangeKey}
            onChange={(_event, value: RangeKey | null) => {
              if (value) {
                updateRange(value);
              }
            }}
            aria-label="Scout dashboard time range"
          >
            {RANGE_OPTIONS.map((option) => (
              <ToggleButton
                key={option.key}
                value={option.key}
                aria-label={option.label}
                disabled={isManualRangeLoading && option.key !== requestedRangeState.rangeKey}
              >
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {showManageChartsPopover && (
            <ManageScoutChartsPopover
              open={showManageChartsPopover}
              anchorEl={manageChartsAnchorEl}
              onClose={() => setManageChartsAnchorEl(null)}
              charts={chartOptions}
              currentConfig={normalizedChartSettings.current}
              savedConfigs={normalizedChartSettings.savedConfigs}
              activeConfigName={normalizedChartSettings.activeConfigName}
              onCurrentConfigChange={handleCurrentChartConfigChange}
              onSaveConfig={handleSaveChartConfig}
              onDeleteConfig={handleDeleteChartConfig}
              onSelectConfig={handleSelectChartConfig}
              onResetConfig={handleResetChartConfig}
            />
          )}
        </Stack>
      </Stack>

      {hasDashboardError && <Alert severity="error">Failed to load Scout dashboard metrics.</Alert>}
      {showEmptyState && <Alert severity="info">No Scout dashboard widgets returned.</Alert>}

      {canShowWidgets && (
        <Box
          aria-busy={isManualRangeLoading}
          data-testid="scout-dashboard-widget-view"
          sx={{
            opacity: isManualRangeLoading ? 0.4 : 1,
            transition: "opacity 150ms ease-in-out",
          }}
        >
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[0, 1, 2, 3].map((index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, lg: 3 }}>
                <ScoutDashboardStatCard
                  widget={statWidgets[index]}
                  loading={isInitialLoading}
                  periodSeconds={dashboardData?.scoutDashboardMetrics.periodSeconds ?? 60}
                />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            {isInitialLoading &&
              Array.from({ length: 12 }).map((_item, index) => (
                <Grid key={`skeleton-${index}`} size={{ xs: 12, lg: 6 }}>
                  <ScoutDashboardTimeSeriesCard loading={true} rangeDurationMs={rangeDurationMs} />
                </Grid>
              ))}
            {!isInitialLoading &&
              visibleChartWidgets.map((widget) => (
                <Grid key={`${widget.id}|${displayRangeKey}`} size={{ xs: 12, lg: 6 }}>
                  <ScoutDashboardTimeSeriesCard
                    reservedSeriesCount={reservedChartSeriesCount}
                    widget={widget}
                    loading={false}
                    rangeDurationMs={rangeDurationMs}
                  />
                </Grid>
              ))}
          </Grid>
        </Box>
      )}
    </Stack>
  );
}
